import axios from 'axios';
import { refreshSession } from './authService';
import { getEnv, isDevelopment, isDebugMode } from '../envConfig';
import { clearAuthSessionStorage } from '../utils/authSessionStorage';

function getApiBaseUrl() {
    const { ENVIRONMENT, ENDPOINT } = getEnv();
    if (ENVIRONMENT === 'development') {
        return '';
    }
    return ENDPOINT || '';
}

let refreshPromise = null;
let isRedirectingToLogin = false;

const axiosInstance = axios.create({
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

function isAuthApiUrl(url = '') {
    return url.includes('/api/auth/') || url.includes('/auth/');
}

function redirectToLoginOnce() {
    if (isRedirectingToLogin || window.location.pathname.startsWith('/login')) {
        return;
    }
    isRedirectingToLogin = true;
    clearAuthSessionStorage();
    window.location.replace('/login');
}

function refreshSessionOnce() {
    if (!refreshPromise) {
        refreshPromise = refreshSession().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

axiosInstance.interceptors.request.use(
    async (config) => {
        config.baseURL = getApiBaseUrl();
        if (isDevelopment() && isDebugMode()) {
            console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
                params: config.params,
                data: config.data,
            });
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => {
        if (isDevelopment() && isDebugMode()) {
            console.log(`API Response: ${response.status} ${response.config.url}`, {
                data: response.data,
            });
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        console.error('API Error:', {
            status: error.response?.status,
            url: error.config?.url,
            method: error.config?.method,
            message: error.message,
            data: error.response?.data,
        });

        const contentType = error.response?.headers?.['content-type'] || '';
        const isHtmlResponse = contentType.toLowerCase().includes('text/html');
        const is401 = error.response?.status === 401;
        const is403 = error.response?.status === 403;
        const isAuthRoute = isAuthApiUrl(originalRequest?.url);

        if (
            !isAuthRoute
            && (is401 || is403 || isHtmlResponse || error.message === 'Network Error')
            && !originalRequest._retry
        ) {
            originalRequest._retry = true;
            try {
                await refreshSessionOnce();
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                redirectToLoginOnce();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
